import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Globe, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, RotateCcw, ExternalLink } from 'lucide-react';

const inputCls = "bg-[#16302B] border-white/10 text-white placeholder:text-white/30 h-10 rounded-xl";
const KIND = { wasi: 'WASI', houzez: 'WordPress Houzez', bela: 'Domus', muv: 'WordPress + Domus', dluxe: 'WordPress', metric: 'Metric', milporciento: 'WordPress JetEngine', linkestate: 'LinkEstate', realhomes: 'WordPress RealHomes' };

const SourceRow = ({ s, onRemove, onRetry }) => (
  <div data-testid={`ext-source-row-${s.key}`} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl bg-[#16302B] border border-white/5">
    <div className="flex-1 min-w-0">
      <p className="text-sm text-white font-medium flex items-center gap-2 truncate">
        {s.status === 'detecting' ? <Loader2 size={14} className="animate-spin text-[#A38560]" />
          : s.status === 'active' ? <CheckCircle2 size={14} className="text-[#6BCB77]" /> : <AlertTriangle size={14} className="text-amber-400" />}
        {s.name}
        <a href={s.base} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-[#A38560]"><ExternalLink size={12} /></a>
      </p>
      <p data-testid={`ext-source-status-${s.key}`} className={`text-[11px] mt-0.5 ${s.status === 'no_compatible' || (s.last && !s.last.ok) ? 'text-amber-400' : 'text-white/40'}`}>
        {s.status === 'detecting' && 'Revisando la página y leyendo inmuebles… (puede tardar unos minutos)'}
        {s.status === 'active' && `${KIND[s.kind] || s.kind} · ${s.last ? (s.last.ok ? `${s.last.count} inmuebles` : `falló la última lectura${s.last.error ? `: ${s.last.error}` : ''}`) : 'sin leer aún'}`}
        {s.status === 'no_compatible' && `No compatible: ${s.error || 'no se pudo leer'}`}
      </p>
    </div>
    <div className="flex gap-2 shrink-0">
      {(s.status === 'no_compatible' || (s.status === 'active' && s.last && !s.last.ok)) && (
        <button onClick={() => onRetry(s)} data-testid={`ext-source-retry-${s.key}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-white/10 text-white/70 hover:text-white">
          <RotateCcw size={12} />Reintentar
        </button>
      )}
      <button onClick={() => onRemove(s)} data-testid={`ext-source-remove-${s.key}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-red-400 hover:bg-red-400/10">
        <Trash2 size={12} />Quitar
      </button>
    </div>
  </div>
);

export const ExternalSourcesManager = () => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: '', url: '' });
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/admin/external-sources').then(setList).catch(() => {});
  useEffect(() => { load(); }, []);
  const detecting = list.some(s => s.status === 'detecting');
  useEffect(() => {
    if (!detecting) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [detecting]);

  const add = async () => {
    if (!form.url.trim()) return toast.error('Escribe la dirección de la página');
    setSaving(true);
    try { await api.post('/admin/external-sources', form); setForm({ name: '', url: '' }); toast.success('Página agregada; la estamos revisando'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar'); }
    setSaving(false);
  };
  const remove = async s => {
    if (!window.confirm(`¿Quitar ${s.name}? Sus inmuebles dejarán de aparecer en los listings.`)) return;
    try { await api.del(`/admin/external-sources/${s.key}`); toast.success('Página quitada'); load(); } catch { toast.error('No se pudo quitar'); }
  };
  const retry = async s => { try { await api.post(`/admin/external-sources/${s.key}/retry`); load(); } catch { toast.error('Error'); } };

  return (
    <div data-testid="external-sources-manager" className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4">
      <h2 className="font-outfit text-lg font-medium text-white flex items-center gap-2"><Globe size={18} className="text-[#A38560]" /> Páginas de otras inmobiliarias</h2>
      <p className="text-sm text-white/40">Páginas donde se buscan inmuebles para los leads (todos los días a las 6 a. m.). Al agregar una, el sistema detecta cómo leerla y empieza a buscar sola.</p>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-2">
        <Input data-testid="ext-source-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre (opcional)" className={inputCls} />
        <Input data-testid="ext-source-url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://inmobiliaria.com" className={inputCls} />
        <button onClick={add} disabled={saving} data-testid="ext-source-add" className="flex items-center justify-center gap-1.5 px-4 h-10 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}Agregar
        </button>
      </div>
      <div className="space-y-2">
        {list.map(s => <SourceRow key={s.key} s={s} onRemove={remove} onRetry={retry} />)}
        {list.length === 0 && <p className="text-sm text-white/40">No hay páginas configuradas.</p>}
      </div>
    </div>
  );
};
